import { LitElement, html, css } from "lit";
import { SignalWatcher, signal } from "@lit-labs/preact-signals";
import { property, state } from "lit/decorators.js";
import {
	loadEntityDefinitions,
	loadToolConfigs,
	mergeAttributes,
	enumOptions,
	type EntityDefinition,
	type MergedAttribute,
	type ToolConfig,
} from "../../services/entity-definition.service";
import { morphedRead, morphedCreate, morphedUpdate, morphedDelete } from "../../services/morphed-entity.service";
import { canView, canCreate, canEdit, canDelete } from "../../services/permission.service";
import { iconX } from "../../utils/icons";
import type { ResolverFn } from "../../services/relation-resolver";
import type { ResolvedRelation } from "../../services/relation-resolver";
import { fileUrl, resolveUrl, type MediaFile, toMediaFile, fromMediaFile, deepParseJsonStrings } from "../../utils/media";
import { toInputValue, fromInputValue } from "../../utils/datetime";
import { resolveDisplayKeys } from "../../utils/relation-display";
import "../clam-dynamic-viewer/clam-dynamic-viewer.js";
import "../clam-relation-autocomplete/clam-relation-autocomplete.js";
import "../clam-object-input/clam-object-input.js";
import "../clam-media-input/clam-media-input.js";

type ViewMode = "table" | "cards" | "accordion";

interface EntityRecord { id: string; [key: string]: unknown; }

const ICON_TABLE = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>`;
const ICON_CARDS = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>`;
const ICON_ACC = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M3 12h18M3 18h18"/><path d="M9 9l3 3-3 3" stroke-linecap="round"/></svg>`;
const ICON_CHEVRON = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>`;
const ICON_IMG_OFF = html`<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 16l5-5 4 4M14 12l3-3 4 4"/><circle cx="9" cy="9" r="1.5"/><path d="M2 2l20 20"/></svg>`;

export class ClamEntityManager extends SignalWatcher(LitElement) {
	static styles = css`
		:host { display: block; font-family: var(--clam-font); color: var(--clam-text); }

		/* === Toolbar === */
		.toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; padding: var(--clam-space-4, 16px); gap: var(--clam-space-3, 12px); }
		.toolbar__left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
		.toolbar__title { font-size: var(--clam-text-xl, 20px); font-weight: 700; color: var(--clam-darkgray, #424244); margin: 0; }
		.toolbar__subtitle { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 0; }
		.toolbar__right { display: flex; align-items: center; gap: var(--clam-space-2, 8px); flex-wrap: wrap; }
		.search-input { padding: 6px var(--clam-space-3, 12px); border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); font-size: var(--clam-text-sm, 13px); width: 100%; max-width: 180px; box-sizing: border-box; font-family: var(--clam-font); }
		.search-input:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }

		.view-switcher { display: flex; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); overflow: hidden; }
		.view-btn { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; background: var(--clam-surface, #fff); border: none; cursor: pointer; color: var(--clam-text-muted, #6d6e71); border-right: 1px solid var(--clam-border, #e2e8f0); transition: all 0.15s; }
		.view-btn:last-child { border-right: none; }
		.view-btn:hover { background: var(--clam-surface-soft, #f5f6f8); }
		.view-btn.active { background: var(--clam-primary, #5a9d1e); color: var(--clam-on-primary, #fff); }

		.btn { padding: 7px var(--clam-space-4, 16px); border-radius: var(--clam-radius-sm, 6px); border: none; cursor: pointer; font-size: var(--clam-text-sm, 13px); font-weight: 500; font-family: var(--clam-font); transition: background 0.15s; white-space: nowrap; }
		.btn-primary { background: var(--clam-light-green, #6bbb24); color: var(--clam-white, #fff); }
		.btn-primary:hover { background: var(--clam-green, #5a9d1e); }
		.btn-danger { background: var(--clam-danger, #ea4335); color: var(--clam-white, #fff); }
		.btn-danger:hover { background: #c83227; }
		.btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
		.btn-sm { padding: 6px 12px; font-size: var(--clam-text-xs, 11px); min-height: 34px; }
		.btn-secondary { background: var(--clam-surface, #fff); color: var(--clam-text, #333); border: 1px solid var(--clam-border, #e2e8f0); }
		.btn-secondary:hover { background: var(--clam-surface-soft, #f5f6f8); }

		/* === Table view === */
		.table-wrap { background: var(--clam-surface, #fff); border-radius: var(--clam-radius-md, 8px); overflow: hidden; box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.08)); margin: 0 var(--clam-space-4, 16px) var(--clam-space-4, 16px); }
		.table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
		table { width: 100%; border-collapse: collapse; font-size: var(--clam-text-sm, 13px); }
		th { background: var(--clam-surface-soft, #f5f6f8); padding: 10px var(--clam-space-3, 12px); text-align: left; font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); font-weight: 600; border-bottom: 2px solid var(--clam-border, #e2e8f0); white-space: nowrap; }
		td { padding: var(--clam-space-2, 8px) var(--clam-space-3, 12px); color: var(--clam-text, #333); border-bottom: 1px solid var(--clam-almost-white, #f0f0f0); max-width: 200px; overflow: hidden; overflow-wrap: anywhere; }
		tr:hover { background: rgba(90,157,30,0.04); }

		/* === Cards view === */
		.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--clam-space-4, 16px); padding: 0 var(--clam-space-4, 16px) var(--clam-space-4, 16px); align-items: start; }
		.card { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-lg, 12px); overflow: hidden; background: var(--clam-surface, #fff); transition: all 0.2s; box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.04)); }
		.card:hover { box-shadow: var(--clam-shadow-md, 0 4px 16px rgba(0,0,0,0.08)); border-color: var(--clam-border, #d0d8e8); }
		.card--expanded { grid-column: span 2; box-shadow: var(--clam-shadow-lg, 0 8px 24px rgba(0,0,0,0.1)); }
		.card__header { position: relative; aspect-ratio: 16/10; background: var(--clam-surface-soft, #f0f2f5); overflow: hidden; }
		.card__header img { width: 100%; height: 100%; object-fit: cover; }
		.card__placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: var(--clam-text-muted, #9ca3af); opacity: 0.3; }
		.card__badge-pos { position: absolute; top: var(--clam-space-2, 8px); right: var(--clam-space-2, 8px); }
		.card__body { display: flex; padding: var(--clam-space-3, 12px); cursor: pointer; gap: var(--clam-space-2, 8px); }
		.card__info { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
		.card__title { font-size: var(--clam-text-sm, 13px); font-weight: 700; color: var(--clam-text, #333); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.card__field { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6b7280); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.card__field-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--clam-text-muted, #9ca3af); }
		.card__chevron { transition: transform 0.2s; color: var(--clam-text-muted, #6b7280); flex-shrink: 0; }
		.card__chevron--up { transform: rotate(180deg); }
		.card__detail { padding: 0 var(--clam-space-3, 12px) var(--clam-space-3, 12px); border-top: 1px solid var(--clam-border, #e2e8f0); }
		.card__detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: var(--clam-space-2, 8px); padding-top: var(--clam-space-2, 8px); }
		.card__detail-item { display: flex; flex-direction: column; gap: 2px; }
		.card__detail-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--clam-text-muted, #9ca3af); }
		.card__detail-actions { display: flex; gap: var(--clam-space-2, 8px); padding-top: var(--clam-space-3, 12px); }

		/* === Accordion view === */
		.accordion { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-lg, 12px); overflow: hidden; box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.04)); margin: 0 var(--clam-space-4, 16px) var(--clam-space-4, 16px); }
		.acc-row { border-bottom: 1px solid var(--clam-border, #e2e8f0); transition: background 0.15s; }
		.acc-row:last-child { border-bottom: none; }
		.acc-row--expanded { background: var(--clam-surface-soft, #f8f9fa); }
		.acc-summary { display: flex; align-items: center; gap: var(--clam-space-3, 12px); padding: var(--clam-space-2, 8px) var(--clam-space-4, 16px); cursor: pointer; }
		.acc-summary:hover { background: rgba(90,157,30,0.04); }
		.acc-thumb { width: 36px; height: 36px; border-radius: var(--clam-radius-sm, 8px); object-fit: cover; flex-shrink: 0; background: var(--clam-surface-soft, #f0f2f5); }
		.acc-title { flex: 1; font-size: var(--clam-text-sm, 13px); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.acc-field { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6b7280); white-space: nowrap; }
		.acc-field-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--clam-text-muted, #9ca3af); }
		.acc-detail { padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px) var(--clam-space-4, 16px); }
		.acc-detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: var(--clam-space-2, 8px); }
		.acc-detail-item { display: flex; flex-direction: column; gap: 2px; }
		.acc-detail-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--clam-text-muted, #9ca3af); }
		.acc-detail-actions { display: flex; gap: var(--clam-space-2, 8px); padding-top: var(--clam-space-3, 12px); }

		/* === Common === */
		.empty { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); font-size: var(--clam-text-base, 14px); }
		.loading { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); display: flex; align-items: center; justify-content: center; gap: 8px; }
		.spinner { display: inline-block; width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.4); border-top-color: currentColor; border-radius: 50%; animation: clmem-spin 0.7s linear infinite; vertical-align: middle; }
		.spinner--inline { width: 16px; height: 16px; border-color: var(--clam-border, #e2e8f0); border-top-color: var(--clam-primary, #5a9d1e); }
		@keyframes clmem-spin { to { transform: rotate(360deg); } }
		/* Singleton: barra de acciones pegada al fondo del viewport (sticky bottom),
		   full-width del card, fondo sólido + sombra para que nada pase por debajo. */
		.singleton-footer {
			position: sticky;
			bottom: 0;
			left: 0; right: 0;
			margin-top: var(--clam-space-5, 20px);
			margin-left: calc(-1 * var(--clam-space-6, 24px));
			margin-right: calc(-1 * var(--clam-space-6, 24px));
			margin-bottom: calc(-1 * var(--clam-space-6, 24px));
			padding: var(--clam-space-3, 12px) var(--clam-space-6, 24px);
			border-top: 1px solid var(--clam-border, #e2e8f0);
			background: var(--clam-surface, #fff);
			box-shadow: 0 -3px 10px rgba(0,0,0,0.07);
			border-radius: 0 0 var(--clam-radius-md, 8px) var(--clam-radius-md, 8px);
			z-index: 5;
		}
		.singleton-footer .modal-actions { justify-content: flex-end; }
		/* Toast de feedback (guardar/crear/error). */
		.toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 300; padding: 12px 20px; border-radius: var(--clam-radius-md, 8px); font-size: var(--clam-text-sm, 13px); font-weight: 600; box-shadow: 0 6px 20px rgba(0,0,0,0.18); max-width: 90vw; }
		.toast--ok { background: #dcfce7; color: #3f7a12; }
		.toast--err { background: #fee2e2; color: #b91c1c; }
		.toast--info { background: #e8f4fd; color: #1565c0; }
		/* Modal pequeño (confirmación borrado) */
		.modal--sm { max-width: 420px; }
		.confirm-text { margin: 0; color: var(--clam-text, #333); font-size: var(--clam-text-base, 14px); line-height: 1.5; }
		/* Checks de selección */
		.row-check { width: 16px; height: 16px; cursor: pointer; accent-color: var(--clam-primary, #5a9d1e); }
		.card { position: relative; }
		.card__select { position: absolute; top: 8px; left: 8px; z-index: 3; background: rgba(255,255,255,0.9); border-radius: 4px; padding: 2px; }
		/* Paginación */
		.pager { display: flex; align-items: center; justify-content: space-between; gap: var(--clam-space-3, 12px); flex-wrap: wrap; padding: var(--clam-space-4, 16px) var(--clam-space-2, 8px); color: var(--clam-text-muted, #6d6e71); font-size: var(--clam-text-sm, 13px); }
		.pager__nav { display: flex; align-items: center; gap: var(--clam-space-2, 8px); }
		.pager__btn { border: 1px solid var(--clam-border, #e2e8f0); background: var(--clam-surface, #fff); color: var(--clam-text, #333); border-radius: var(--clam-radius-sm, 6px); padding: 6px 12px; cursor: pointer; font-size: var(--clam-text-sm, 13px); font-family: inherit; }
		.pager__btn:hover:not(:disabled) { background: var(--clam-surface-soft, #f5f6f8); }
		.pager__btn:disabled { opacity: 0.4; cursor: not-allowed; }
		.pager__page { font-weight: 600; color: var(--clam-text, #333); }
		.pager__size { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); padding: 4px 8px; font-size: var(--clam-text-sm, 13px); font-family: inherit; background: var(--clam-surface, #fff); color: var(--clam-text, #333); }

		/* === Modal === */
		.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 200; padding: var(--clam-space-4, 16px); }
		.modal { background: var(--clam-surface, #fff); border-radius: var(--clam-radius-lg, 12px); width: 100%; max-width: 700px; max-height: calc(100dvh - 32px); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.2); }
		.modal form { display: flex; flex-direction: column; min-height: 0; flex: 1; }
		.modal__header { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: var(--clam-space-3, 12px); padding: var(--clam-space-4, 16px) var(--clam-space-5, 20px); border-bottom: 1px solid var(--clam-border, #e2e8f0); }
		.modal__header h2 { margin: 0; color: var(--clam-darkgray, #424244); font-size: var(--clam-text-lg, 18px); overflow: hidden; text-overflow: ellipsis; }
		.modal__close { flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer; background: transparent; color: var(--clam-text-muted, #6d6e71); font-size: 18px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
		.modal__close:hover { background: var(--clam-surface-soft, #f0f2f5); color: var(--clam-text, #333); }
		.modal__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: var(--clam-space-5, 20px); }
		.modal__footer { flex-shrink: 0; padding: var(--clam-space-3, 12px) var(--clam-space-5, 20px); border-top: 1px solid var(--clam-border, #e2e8f0); background: var(--clam-surface, #fff); position: sticky; bottom: 0; }
		.singleton-form { background: var(--clam-surface, #fff); border-radius: var(--clam-radius-md, 8px); box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.08)); padding: var(--clam-space-6, 24px); margin: 0 var(--clam-space-4, 16px) var(--clam-space-4, 16px); }
		.form-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--clam-space-4, 16px); }
		.form-group { display: flex; flex-direction: column; }
		.form-group.full { grid-column: 1 / -1; }
		.form-group label { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin-bottom: var(--clam-space-1, 4px); font-weight: 500; }
		.form-group--section { margin-top: var(--clam-space-2, 8px); }
		.form-section-title {
			font-size: var(--clam-text-lg, 17px); font-weight: 700; color: var(--clam-darkgray, #424244);
			margin: 0 0 var(--clam-space-3, 12px); padding-bottom: var(--clam-space-2, 8px);
			border-bottom: 1px solid var(--clam-border, #e2e8f0);
		}
		.form-group input, .form-group textarea, .form-group select { width: 100%; padding: 7px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 5px); font-size: var(--clam-text-sm, 13px); font-family: var(--clam-font); box-sizing: border-box; }
		.form-group input:focus, .form-group textarea:focus, .form-group select:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
		.modal-actions { display: flex; gap: var(--clam-space-3, 12px); justify-content: flex-end; }

		/* === Detail modal === */
		.detail-modal { max-width: 760px; }
		.detail-modal__head { gap: var(--clam-space-3, 12px); }
		.detail-modal__img { width: 56px; height: 56px; border-radius: var(--clam-radius-sm, 8px); object-fit: cover; flex-shrink: 0; border: 1px solid var(--clam-border, #e2e8f0); }
		.detail-modal__headinfo { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
		.detail-modal__headinfo h2 { margin: 0; font-size: var(--clam-text-lg, 18px); color: var(--clam-darkgray, #424244); overflow: hidden; text-overflow: ellipsis; }
		.detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--clam-space-3, 12px) var(--clam-space-4, 16px); }
		.detail-grid__item { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
		.detail-grid__label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--clam-text-muted, #9ca3af); }
		.detail-grid__value { font-size: var(--clam-text-sm, 13px); color: var(--clam-text, #333); word-break: break-word; overflow-wrap: anywhere; }
		.detail-grid__item--full { grid-column: 1 / -1; }
		@media (max-width: 640px) { .detail-grid { grid-template-columns: 1fr; } }

		@media (min-width: 640px) {
			.form-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
			.search-input { width: 200px; max-width: 200px; }
			.toolbar { padding: var(--clam-space-5, 20px) var(--clam-space-6, 24px); }
			.cards-grid { padding: 0 var(--clam-space-6, 24px) var(--clam-space-6, 24px); }
			.table-wrap, .accordion { margin: 0 var(--clam-space-6, 24px) var(--clam-space-6, 24px); }
		}
		@media (max-width: 768px) {
			.cards-grid { grid-template-columns: 1fr; }
			.card--expanded { grid-column: span 1; }
		}
		@media (max-width: 640px) {
			/* Singleton form más compacto en móvil. */
			.singleton-form { padding: var(--clam-space-4, 16px); margin-left: var(--clam-space-3, 12px); margin-right: var(--clam-space-3, 12px); }
			.singleton-footer {
				margin-left: calc(-1 * var(--clam-space-4, 16px));
				margin-right: calc(-1 * var(--clam-space-4, 16px));
				margin-bottom: calc(-1 * var(--clam-space-4, 16px));
				padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px);
			}
			/* Botón Guardar full-width en móvil (más fácil de tocar). */
			.singleton-footer .modal-actions { justify-content: stretch; }
			.singleton-footer .btn { flex: 1; min-height: 44px; }
		}
	`;

	@property({ attribute: false }) entityEndpoint = "";

	private defs: EntityDefinition[] = [];
	private tools: ToolConfig[] = [];
	private records = signal<EntityRecord[]>([]);
	private loading = signal(false);
	private showForm = signal(false);
	private search = signal("");
	private viewMode = signal<ViewMode>(typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches ? "cards" : "table");
	private detailRecord = signal<EntityRecord | null>(null);
	private editingRecord: EntityRecord | null = null;
	private formData: Record<string, unknown> = {};
	private _saving = false;
	private _toast: { kind: "ok" | "err" | "info"; text: string } | null = null;
	private _toastTimer: number | null = null;
	private _selectedIds = new Set<string>();
	private _page = 0;
	private _pageSize = 10;
	// @state() para que Lit re-renderice automáticamente al asignarlos.
	// Antes eran campos privados planos → el botón Cancelar del modal asignaba null
	// pero no disparaba re-render → el modal quedaba atrapado.
	@state() private _deleteTarget: { ids: string[]; label: string } | null = null;
	@state() private _deleting = false;
	private resolverFn: ResolverFn | undefined;
	private relatedCache: Map<string, Map<string, ResolvedRelation>> = new Map();
	private _currentEndpoint = "";
	private _showWhenValues: Map<string, unknown> = new Map();
	private _relatedRecCache: Map<string, Record<string, unknown>> = new Map();

	async updated() {
		if (this.entityEndpoint && this.entityEndpoint !== this._currentEndpoint) {
			this._currentEndpoint = this.entityEndpoint;
			this.showForm.value = false;
			this.editingRecord = null;
			this.formData = {};
			this.detailRecord.value = null;
			this._selectedIds = new Set();
			this._page = 0;
			this._deleteTarget = null;
			await this._ensureDefs();
			await this.loadRecords();
		}
	}

	private async _ensureDefs() {
		if (this.defs.length === 0) {
			this.defs = await loadEntityDefinitions();
			this.tools = await loadToolConfigs();
			const tool = this.tools.find((t) => t.entityName === this.activeDef?.entityName);
			const dv = tool?.defaultView as ViewMode | undefined;
			if (dv === "table" || dv === "cards" || dv === "accordion") this.viewMode.value = dv;
			this.requestUpdate();
		}
	}

	private get activeDef(): EntityDefinition | undefined {
		return this.defs.find((d) => d.entityEndpoint === this.entityEndpoint);
	}

	private get mergedAttrs(): MergedAttribute[] {
		const def = this.activeDef;
		if (!def) return [];
		const tool = this.tools.find((t) => t.entityName === def.entityName) || null;
		return mergeAttributes(def, tool);
	}

 	private async loadRecords() {
		const def = this.activeDef;
		if (!def) return;
		// Sin permiso de lectura (canView) para este tool: no cargar registros.
		// El render() muestra el mensaje "sin permiso de lectura".
		if (!canView(def.entityEndpoint)) {
			this.records.value = [];
			this.loading.value = false;
			return;
		}
		this.loading.value = true;
		try {
			// relations=false: morphed-be NO resuelve el árbol de relaciones anidadas
			// (que para clam-invitacion/persona trae O(N×M) datos y timeouta en prod).
			// Los labels legibles se cargan vía preloadRelations() abajo.
			const raw = await morphedRead<EntityRecord>(def.entityName, {
				options: { limit: 500 },
				relations: false,
				allowPublic: def.public,
			});
			// morphed stringifica atributos object/file-upload al guardar; normalizamos al
			// cargar para que el form trabaje con objetos limpios (rompe el ciclo de stringify progresivo).
			this.records.value = raw.map((r) => deepParseJsonStrings(r) as EntityRecord);
			await this.preloadRelations();
			if (def.entityType === "Singleton") {
				const rec = this.records.value[0] ?? null;
				this.editingRecord = rec;
				if (rec) {
					this.formData = JSON.parse(JSON.stringify(rec));
				} else {
					this.formData = {};
					for (const a of this.editableAttrs) this.formData[a.attributeKey] = this.defaultFor(a);
				}
			}
		} catch (e) { console.error("loadRecords:", e); this.records.value = []; }
		this.loading.value = false;
	}

	private async preloadRelations() {
		const attrs = this.mergedAttrs;
		const relEntities = new Set<string>();
		for (const a of attrs) if (a.relatedWith) relEntities.add(a.relatedWith);
		this.relatedCache.clear();
		for (const en of relEntities) {
			try {
				const rd = this.defs.find((d) => d.entityName === en);
				// relations=false aquí también: solo necesitamos ID + displayKey para armar labels.
				const recs = await morphedRead<Record<string, unknown>>(en, {
					options: { limit: 500 },
					relations: false,
					allowPublic: rd?.public ?? true,
				});
				// displayKey + secondaryKeys canonico del relacionado (para label y campos extra).
				const { displayKey, secondaryKeys } = resolveDisplayKeys(rd);
				const c = new Map<string, ResolvedRelation>();
				for (const r of recs) {
					const label = String(r[displayKey] || r.name || r.eventName || r.id).substring(0, 60);
					const secondary = secondaryKeys.map((k) => (r[k] ? String(r[k]).substring(0, 40) : "")).filter(Boolean);
					c.set(r.id as string, { label, secondary });
				}
				this.relatedCache.set(en, c);
			} catch { /* skip */ }
		}
		this.resolverFn = (en: string, id: string) => this.relatedCache.get(en)?.get(id);
	}

	private get filteredRecords(): EntityRecord[] {
		const q = this.search.value.toLowerCase().trim();
		let recs = !q ? this.records.value : this.records.value.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
		if (this.mergedAttrs.some((a) => a.attributeKey === "sortOrder")) {
			recs = [...recs].sort((a, b) => ((a.sortOrder as number) ?? 1e9) - ((b.sortOrder as number) ?? 1e9));
		}
		return recs;
	}

	private get visibleColumns(): MergedAttribute[] {
		return this.mergedAttrs.filter((a) => !a.isHidden && a.isVisibleByDefault && a.effectiveInputType !== "no-input").slice(0, 7);
	}

	private get editableAttrs(): MergedAttribute[] {
		return this.mergedAttrs.filter((a) => !a.isHidden && a.effectiveInputType !== "system-generated" && a.effectiveInputType !== "no-input" && a.attributeKey !== "id" && a.isEditable !== false);
	}

	private get activeToolConfig(): ToolConfig | undefined {
		return this.tools.find((t) => t.entityName === this.activeDef?.entityName);
	}

	private get displayCfg(): ToolConfig["displayConfig"] | null {
		return this.activeToolConfig?.displayConfig ?? null;
	}

	private get thumbAttr(): MergedAttribute | undefined {
		const dc = this.displayCfg;
		if (dc) {
			const ci = dc.cardImage as { attrKey?: string } | string | undefined;
			const key = typeof ci === "string" ? ci : ci?.attrKey;
			if (key) return this.mergedAttrs.find((a) => a.attributeKey === key);
		}
		return this.visibleColumns.find((a) => a.effectiveOutputType === "image");
	}
	private get titleAttr(): MergedAttribute | undefined {
		const dc = this.displayCfg;
		const key = dc?.cardTitle as string | undefined;
		if (key) return this.mergedAttrs.find((a) => a.attributeKey === key);
		return this.visibleColumns.find((a) => a.attributeKey.toLowerCase().includes("name") || a.attributeKey.toLowerCase().includes("title")) || this.visibleColumns[0];
	}
	private get statusAttr(): MergedAttribute | undefined {
		return this.visibleColumns.find((a) => a.effectiveOutputType === "status");
	}
	private get summaryFields(): MergedAttribute[] {
		const dc = this.displayCfg;
		const skip = new Set([this.thumbAttr?.attributeKey, this.titleAttr?.attributeKey, this.statusAttr?.attributeKey].filter(Boolean) as string[]);
		const cardFields = dc?.cardFields as string[] | undefined;
		if (cardFields) {
			return cardFields.map((k) => this.mergedAttrs.find((a) => a.attributeKey === k)).filter(Boolean) as MergedAttribute[];
		}
		return this.visibleColumns.filter((a) => !skip.has(a.attributeKey)).slice(0, 3);
	}

	private cardImage(item: EntityRecord): string {
		const ci = this.displayCfg?.cardImage;
		if (ci) {
			const cfg = typeof ci === "string"
				? { attrKey: ci }
				: (ci as { attrKey: string; urlKey?: string; filterKey?: string; filterValue?: string });
			const root = item[cfg.attrKey];
			if (Array.isArray(root)) {
				const el = cfg.filterKey && cfg.filterValue
					? root.find((r) => String((r as Record<string, unknown>)?.[cfg.filterKey as string]) === String(cfg.filterValue))
					: root[0] as Record<string, unknown> | undefined;
				if (!el) return "";
				return fileUrl(cfg.urlKey ? el[cfg.urlKey] : el);
			}
			if (root && typeof root === "object") {
				return fileUrl(cfg.urlKey ? (root as Record<string, unknown>)[cfg.urlKey] : root);
			}
			return fileUrl(root);
		}
		if (this.thumbAttr) return fileUrl(item[this.thumbAttr.attributeKey]);
		return "";
	}

	private defaultFor(attr: MergedAttribute): unknown {
		const ent = this.activeDef?.entityName ?? "";
		if (attr.attributeKey === "active" && (ent === "Clam Asociado" || ent === "Clam Subcomite" || ent === "Clam Noticia")) return true;
		if (attr.morphedVariableType === "boolean") return false;
		if (attr.morphedVariableType === "number") return attr.attributeKey === "sortOrder" ? 0 : 0;
		if (["idArray", "stringArray", "objectArray"].includes(attr.morphedVariableType)) return [];
		if (attr.morphedAttributeType === "nested" && attr.morphedVariableType === "object") return {};
		return "";
	}

	private get displayTitle(): string {
		return this.activeToolConfig?.toolLabel ?? this.activeDef?.entityName.replace("Clam ", "") ?? "";
	}

	private openCreate() { this.editingRecord = null; this.formData = {}; for (const a of this.editableAttrs) this.formData[a.attributeKey] = this.defaultFor(a); this.showForm.value = true; void this._evalShowWhen(); }
	private openEdit(r: EntityRecord) {
		this.editingRecord = JSON.parse(JSON.stringify(r));
		this.formData = JSON.parse(JSON.stringify(r));
		// Normalizar campos date/datetime/time a ISO: el valor crudo puede venir en
		// formato RFC HTTP (ej. "Fri, 31 Jul 2026 09:00:00 GMT") y, si el usuario no
		// modifica el campo, se enviaría tal cual → morphed lo rechaza (no es ISO 8601).
		for (const a of this.editableAttrs) {
			const it = a.effectiveInputType;
			if (it !== "datetime" && it !== "date" && it !== "time") continue;
			const raw = this.formData[a.attributeKey];
			if (raw === null || raw === undefined || raw === "") continue;
			const norm = fromInputValue(toInputValue(raw, a, it), a, it);
			if (norm !== null) this.formData[a.attributeKey] = norm;
		}
		this.showForm.value = true;
		void this._evalShowWhen();
	}

	/** Abre el modal de confirmación de borrado (propio, no confirm() del navegador). */
	private deleteRecord(id: string) {
		const def = this.activeDef; if (!def) return;
		const rec = this.records.value.find((r) => r.id === id);
		const label = rec ? String(this.titleAttr ? rec[this.titleAttr.attributeKey] : rec.eventName || rec.name || rec.id) : id;
		this._deleteTarget = { ids: [id], label };
		this.requestUpdate();
	}
	private deleteSelected() {
		if (this._selectedIds.size === 0) return;
		this._deleteTarget = { ids: [...this._selectedIds], label: `${this._selectedIds.size} registro(s)` };
		this.requestUpdate();
	}
	private async _confirmDelete() {
		const def = this.activeDef; if (!def || !this._deleteTarget) return;
		this._deleting = true; this.requestUpdate();
		try {
			await morphedDelete(def.entityName, this._deleteTarget.ids);
			for (const id of this._deleteTarget.ids) this._selectedIds.delete(id);
			this._selectedIds = new Set(this._selectedIds);
			this._deleteTarget = null;
			this._showToast("ok", "Registro(s) eliminado(s).");
			await this.loadRecords();
		} catch (e) {
			this._showToast("err", `Error al eliminar: ${e instanceof Error ? e.message : String(e)}`);
		} finally {
			this._deleting = false;
			this.requestUpdate();
		}
	}

	/* === Selección múltiple === */
	private _toggleSelect(id: string, on: boolean) {
		if (on) this._selectedIds.add(id); else this._selectedIds.delete(id);
		this._selectedIds = new Set(this._selectedIds); // nueva ref para reactividad
		this.requestUpdate();
	}
	private _toggleSelectAll(on: boolean) {
		this._selectedIds = on ? new Set(this.filteredRecords.map((r) => r.id)) : new Set();
		this.requestUpdate();
	}
	private get _allOnPageSelected(): boolean {
		const page = this._pagedRecords;
		return page.length > 0 && page.every((r) => this._selectedIds.has(r.id));
	}

	/* === Paginación === */
	private get _pagedRecords(): EntityRecord[] {
		const recs = this.filteredRecords;
		const page = Math.min(this._page, this._totalPages - 1);
		const start = page * this._pageSize;
		return recs.slice(start, start + this._pageSize);
	}
	private get _totalPages(): number { return Math.max(1, Math.ceil(this.filteredRecords.length / this._pageSize)); }
	private _setPage(p: number) { this._page = Math.max(0, Math.min(p, this._totalPages - 1)); this.requestUpdate(); }

	private updateFormField(k: string, v: unknown) { this.formData = { ...this.formData, [k]: v }; this.requestUpdate(); void this._evalShowWhen(); }

	/** showWhen: oculta un atributo segun el valor de un atributo relacionado (ej. accessLink solo si evento es virtual). */
	private _getShowWhen(attr: MergedAttribute): { relatedAttr: string; field: string; equals: unknown } | undefined {
		const opts = (attr.morphedInputTypeOptions || []) as Array<Record<string, unknown>>;
		const found = opts.find((o) => o && o.option === "showWhen");
		const v = found?.value as { relatedAttr?: string; field?: string; equals?: unknown } | undefined;
		if (v && v.relatedAttr && v.field) return { relatedAttr: v.relatedAttr, field: v.field, equals: v.equals };
		return undefined;
	}

	private async _evalShowWhen() {
		const def = this.activeDef;
		if (!def) return;
		for (const attr of this.editableAttrs) {
			const sw = this._getShowWhen(attr);
			if (!sw) continue;
			const relatedId = this.formData[sw.relatedAttr];
			const cacheKey = `${sw.relatedAttr}.${sw.field}`;
			if (!relatedId) { this._showWhenValues.delete(cacheKey); continue; }
			const relAttrDef = this.mergedAttrs.find((a) => a.attributeKey === sw.relatedAttr);
			const relEntity = relAttrDef?.relatedWith;
			if (!relEntity) continue;
			const recKey = `${relEntity}:${relatedId}`;
			let rec = this._relatedRecCache.get(recKey);
			if (!rec) {
				try {
					const recs = await morphedRead<Record<string, unknown>>(relEntity, { filters: [{ id: relatedId }], options: { limit: 1 }, allowPublic: true });
					rec = recs[0];
					if (rec) this._relatedRecCache.set(recKey, rec);
				} catch { /* ignore */ }
			}
			if (rec) this._showWhenValues.set(cacheKey, rec[sw.field]);
			else this._showWhenValues.delete(cacheKey);
		}
		this.requestUpdate();
	}

	private _shouldShowAttr(attr: MergedAttribute): boolean {
		const sw = this._getShowWhen(attr);
		if (!sw) return true;
		return this._showWhenValues.get(`${sw.relatedAttr}.${sw.field}`) === sw.equals;
	}

	private _showToast(kind: "ok" | "err" | "info", text: string) {
		this._toast = { kind, text };
		if (this._toastTimer) window.clearTimeout(this._toastTimer);
		this._toastTimer = window.setTimeout(() => { this._toast = null; this.requestUpdate(); }, kind === "err" ? 6000 : 3500);
		this.requestUpdate();
	}

	private async saveForm(ev: SubmitEvent) {
		ev.preventDefault();
		const def = this.activeDef; if (!def) return;
		const vk = new Set(def.mappedAttributes.map((a) => a.attributeKey));
		const cd: Record<string, unknown> = {};
		for (const [k, v] of Object.entries(this.formData)) if (vk.has(k)) cd[k] = v;
		this._saving = true;
		this.requestUpdate();
		try {
			if (this.editingRecord) { await morphedUpdate(def.entityName, this.editingRecord.id, cd); this._showToast("ok", "Cambios guardados."); }
			else { await morphedCreate(def.entityName, [cd]); this._showToast("ok", "Registro creado."); }
			this.showForm.value = false;
			await this.loadRecords();
		} catch (e) {
			this._showToast("err", `Error al guardar: ${e instanceof Error ? e.message : String(e)}`);
		} finally {
			this._saving = false;
			this.requestUpdate();
		}
	}

	private openDetail(r: EntityRecord) { this.detailRecord.value = r; this.requestUpdate(); }
	private closeDetail() { this.detailRecord.value = null; this.requestUpdate(); }

	private renderField(attr: MergedAttribute, value: unknown, onChange: (v: unknown) => void) {
		const isSection = attr.effectiveInputType === "object" || attr.effectiveInputType === "objectArray";
		const labelText = `${attr.label ?? attr.attributeName}${attr.isRequired ? " *" : ""}`;
		const label = isSection
			? html`<div class="form-section-title">${labelText}</div>`
			: html`<label>${labelText}</label>`;
		const fw = ["text-area", "object", "objectArray", "autocomplete", "file-upload"].includes(attr.effectiveInputType) || attr.morphedVariableType === "stringArray";
		let input: unknown;
		if (attr.effectiveInputType === "autocomplete" && attr.relatedWith) {
			input = html`<clam-relation-autocomplete .relatedEntity=${attr.relatedWith} .multi=${attr.morphedVariableType === "idArray"} .placeholder="Buscar..." .value=${value as string | string[] | null} @change=${(e: CustomEvent) => onChange(e.detail)}></clam-relation-autocomplete>`;
		} else if (attr.effectiveInputType === "file-upload") {
			const accept = /video|mp4/i.test(attr.attributeKey) ? "video/*" : /image|img|logo|hero|photo|avatar|thumbnail|banner|foto|picture|perfil|portrait/i.test(attr.attributeKey) ? "image/*" : "*/*";
			const isMulti = attr.morphedVariableType === "objectArray" || attr.morphedVariableType === "stringArray";
			const fk = attr.fileKeys;
			const translated = fk
				? (isMulti
					? (Array.isArray(value) ? value.map((v) => toMediaFile(v, fk)) : [])
					: toMediaFile(value, fk))
				: (value as MediaFile | MediaFile[] | null);
			input = html`<clam-media-input .accept=${accept} .multi=${isMulti} .value=${translated ?? null} @change=${(e: CustomEvent) => {
				if (!fk) { onChange(e.detail); return; }
				const d = e.detail;
				onChange(isMulti ? (Array.isArray(d) ? d.map((m: MediaFile) => fromMediaFile(m, fk)) : []) : fromMediaFile(d as MediaFile, fk));
			}}></clam-media-input>`;
		} else if (attr.effectiveInputType === "object" || attr.effectiveInputType === "objectArray") {
			input = html`<clam-object-input .attr=${attr} .model=${{ [attr.attributeKey]: value || (attr.effectiveInputType === "objectArray" ? [] : {}) }} @change=${(e: CustomEvent) => onChange((e.detail as Record<string, unknown>)[attr.attributeKey])}></clam-object-input>`;
		} else if (attr.effectiveInputType === "select") {
			const opts = enumOptions(attr);
			input = html`<select @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}><option value="">—</option>${opts.map((o) => html`<option value=${o} ?selected=${value === o}>${o}</option>`)}</select>`;
		} else if (attr.effectiveInputType === "switch" || attr.morphedVariableType === "boolean") {
			input = html`<select @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value === "true")}><option value="false" ?selected=${!value}>No</option><option value="true" ?selected=${value}>Sí</option></select>`;
		} else if (attr.effectiveInputType === "number") {
			input = html`<input type="number" .value=${value ?? ""} @input=${(e: InputEvent) => onChange(Number((e.target as HTMLInputElement).value))} />`;
		} else if (attr.effectiveInputType === "text-area" || attr.morphedVariableType === "stringArray") {
			const v = Array.isArray(value) ? value.join("\n") : String(value || "");
			input = html`<textarea rows="3" .value=${v} @input=${(e: InputEvent) => { const r = (e.target as HTMLTextAreaElement).value; onChange(attr.morphedVariableType === "stringArray" ? r.split("\n").filter(Boolean) : r); }}></textarea>`;
		} else if (attr.effectiveInputType === "datetime") {
			input = html`<input type="datetime-local" .value=${toInputValue(value, attr, "datetime")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "datetime"))} />`;
		} else if (attr.effectiveInputType === "date") {
			input = html`<input type="date" .value=${toInputValue(value, attr, "date")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "date"))} />`;
		} else if (attr.effectiveInputType === "time") {
			input = html`<input type="time" .value=${toInputValue(value, attr, "time")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "time"))} />`;
		} else if (attr.effectiveInputType === "email") {
			input = html`<input type="email" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		} else if (attr.effectiveInputType === "password") {
			input = html`<input type="password" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		} else if (attr.effectiveInputType === "url") {
			input = html`<input type="url" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		} else { input = html`<input type="text" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`; }
		return html`<div class="form-group ${fw ? "full" : ""} ${isSection ? "form-group--section" : ""}">${label}${input}</div>`;
	}

	render() {
		const def = this.activeDef;
		if (!def) return html`<div class="empty">Selecciona una entidad</div>`;
		if (def.entityType === "Singleton") return this.renderSingletonView();
		const recs = this.filteredRecords;

		return html`
			<div class="toolbar">
				<div class="toolbar__left">
					<h1 class="toolbar__title">${this.displayTitle}</h1>
					<p class="toolbar__subtitle">${def.entityDescription} · ${recs.length} registros${this._selectedIds.size ? ` · ${this._selectedIds.size} seleccionado(s)` : ""}</p>
				</div>
				<div class="toolbar__right">
					<input class="search-input" type="text" placeholder="Buscar..." .value=${this.search.value}
						@input=${(e: InputEvent) => { this.search.value = (e.target as HTMLInputElement).value; this._page = 0; this.requestUpdate(); }} />
					<div class="view-switcher">
						<button class="view-btn ${this.viewMode.value === "table" ? "active" : ""}" @click=${() => { this.viewMode.value = "table"; this.requestUpdate(); }} title="Tabla">${ICON_TABLE}</button>
						<button class="view-btn ${this.viewMode.value === "cards" ? "active" : ""}" @click=${() => { this.viewMode.value = "cards"; this.requestUpdate(); }} title="Cards">${ICON_CARDS}</button>
						<button class="view-btn ${this.viewMode.value === "accordion" ? "active" : ""}" @click=${() => { this.viewMode.value = "accordion"; this.requestUpdate(); }} title="Acordeón">${ICON_ACC}</button>
					</div>
					${this._selectedIds.size > 0 && canDelete(this.entityEndpoint) ? html`<button class="btn btn-danger" @click=${() => this.deleteSelected()}>Eliminar (${this._selectedIds.size})</button>` : null}
					${canCreate(this.entityEndpoint) ? html`<button class="btn btn-primary" @click=${() => this.openCreate()}>+ Nuevo</button>` : null}
				</div>
			</div>

		${!canView(this.entityEndpoint) ? html`<div class="empty">No tienes permiso de lectura para esta herramienta.</div>`
		: this.loading.value ? html`<div class="loading"><span class="spinner spinner--inline"></span> Cargando...</div>`
		: recs.length === 0 ? html`<div class="empty">Sin registros</div>`
		: this.viewMode.value === "table" ? this.renderTable(this._pagedRecords)
		: this.viewMode.value === "cards" ? this.renderCards(this._pagedRecords)
		: this.renderAccordion(this._pagedRecords)}
		${recs.length > this._pageSize ? this.renderPagination() : null}
		${this.showForm.value ? this.renderForm() : null}
		${this.detailRecord.value ? this.renderDetailModal() : null}
		${this._deleteTarget ? this.renderDeleteModal() : null}
		${this._toast ? html`<div class="toast toast--${this._toast.kind}">${this._toast.text}</div>` : null}
	`;
	}

	private renderPagination() {
		const total = this.filteredRecords.length;
		const from = this._page * this._pageSize + 1;
		const to = Math.min(total, (this._page + 1) * this._pageSize);
		return html`
			<div class="pager">
				<span class="pager__info">${from}-${to} de ${total}</span>
				<div class="pager__nav">
					<button class="pager__btn" ?disabled=${this._page <= 0} @click=${() => this._setPage(this._page - 1)}>‹ Anterior</button>
					<span class="pager__page">Página ${this._page + 1} / ${this._totalPages}</span>
					<button class="pager__btn" ?disabled=${this._page >= this._totalPages - 1} @click=${() => this._setPage(this._page + 1)}>Siguiente ›</button>
				</div>
				<select class="pager__size" @change=${(e: Event) => { this._pageSize = Number((e.target as HTMLSelectElement).value); this._page = 0; this.requestUpdate(); }}>
					${[10, 20, 50].map((n) => html`<option value=${n} ?selected=${this._pageSize === n}>${n} / pág</option>`)}
				</select>
			</div>`;
	}

	private renderDeleteModal() {
		const t = this._deleteTarget!;
		return html`
			<div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget && !this._deleting) this._deleteTarget = null; }}>
				<div class="modal modal--sm">
					<div class="modal__header"><h2>Confirmar eliminación</h2></div>
					<div class="modal__body">
						<p class="confirm-text">¿Seguro que deseas eliminar <strong>${t.label}</strong>? Esta acción no se puede deshacer.</p>
					</div>
					<div class="modal__footer">
						<div class="modal-actions">
							<button class="btn btn-secondary" ?disabled=${this._deleting} @click=${() => (this._deleteTarget = null)}>Cancelar</button>
							<button class="btn btn-danger" ?disabled=${this._deleting} @click=${() => this._confirmDelete()}>${this._deleting ? html`<span class="spinner"></span> Eliminando...` : "Eliminar"}</button>
						</div>
					</div>
				</div>
			</div>`;
	}

	private renderTable(records: EntityRecord[]) {
		const cols = this.visibleColumns;
		const allSel = this._allOnPageSelected;
		const showSel = canDelete(this.entityEndpoint);
		const showActions = canEdit(this.entityEndpoint) || canDelete(this.entityEndpoint);
		return html`<div class="table-wrap"><div class="table-scroll"><table>
			<thead><tr>
				${showSel ? html`<th style="width:34px"><input type="checkbox" class="row-check" title="Seleccionar todo" .checked=${allSel} @change=${(e: Event) => this._toggleSelectAll((e.target as HTMLInputElement).checked)} /></th>` : null}
				${cols.map((c) => html`<th>${c.attributeName}</th>`)}${showActions ? html`<th style="width:110px">Acciones</th>` : null}</tr></thead>
			<tbody>${records.map((r) => html`<tr>
				${showSel ? html`<td><input type="checkbox" class="row-check" .checked=${this._selectedIds.has(r.id)} @change=${(e: Event) => this._toggleSelect(r.id, (e.target as HTMLInputElement).checked)} /></td>` : null}
				${cols.map((c) => html`<td><clam-dynamic-viewer .attr=${c} .value=${r[c.attributeKey]} .resolver=${this.resolverFn}></clam-dynamic-viewer></td>`)}
				${showActions ? html`<td>${canEdit(this.entityEndpoint) ? html`<button class="btn btn-sm btn-secondary" @click=${() => this.openEdit(r)}>Editar</button>` : null} ${canDelete(this.entityEndpoint) ? html`<button class="btn btn-sm btn-danger" @click=${() => this.deleteRecord(r.id)}>Eliminar</button>` : null}</td>` : null}
			</tr>`)}</tbody>
		</table></div></div>`;
	}

	private renderCards(records: EntityRecord[]) {
		return html`<div class="cards-grid">${records.map((r) => {
			const img = this.cardImage(r);
			return html`<div class="card">
				${canDelete(this.entityEndpoint) ? html`<div class="card__select"><input type="checkbox" class="row-check" .checked=${this._selectedIds.has(r.id)} @change=${(e: Event) => this._toggleSelect(r.id, (e.target as HTMLInputElement).checked)} /></div>` : null}
				<div class="card__header">
					${img ? html`<img src=${img} alt="" />` : html`<div class="card__placeholder">${ICON_IMG_OFF}</div>`}
					${this.statusAttr ? html`<div class="card__badge-pos"><clam-dynamic-viewer .attr=${this.statusAttr} .value=${r[this.statusAttr.attributeKey]} .resolver=${this.resolverFn}></clam-dynamic-viewer></div>` : null}
				</div>
				<div class="card__body" @click=${() => this.openDetail(r)}>
					<div class="card__info">
						${this.titleAttr ? html`<span class="card__title">${String(r[this.titleAttr.attributeKey] || "—")}</span>` : null}
						${this.summaryFields.map((f) => html`<span class="card__field"><span class="card__field-label">${f.attributeName}:</span> <clam-dynamic-viewer .attr=${f} .value=${r[f.attributeKey]} .resolver=${this.resolverFn}></clam-dynamic-viewer></span>`)}
					</div>
					<span class="card__chevron">${ICON_CHEVRON}</span>
				</div>
			</div>`;
		})}</div>`;
	}

	private renderAccordion(records: EntityRecord[]) {
		return html`<div class="accordion">${records.map((r) => {
			const img = this.cardImage(r);
			return html`<div class="acc-row">
				<div class="acc-summary" @click=${() => this.openDetail(r)}>
					${img ? html`<img class="acc-thumb" src=${img} alt="" />` : null}
					<span class="acc-title">${this.titleAttr ? String(r[this.titleAttr.attributeKey] || "—") : r.id}</span>
					${this.statusAttr ? html`<clam-dynamic-viewer .attr=${this.statusAttr} .value=${r[this.statusAttr.attributeKey]} .resolver=${this.resolverFn}></clam-dynamic-viewer>` : null}
					${this.summaryFields.map((f) => html`<span class="acc-field"><span class="acc-field-label">${f.attributeName}:</span> ${String(r[f.attributeKey] ?? "—").substring(0, 20)}</span>`)}
					<span class="card__chevron">${ICON_CHEVRON}</span>
				</div>
			</div>`;
		})}</div>`;
	}

	private get detailAttrs(): MergedAttribute[] {
		return this.mergedAttrs.filter((a) => !a.isHidden && a.effectiveInputType !== "system-generated" && a.effectiveInputType !== "no-input" && a.attributeKey !== "id");
	}

	private isMediaAttr(a: MergedAttribute): boolean {
		return ["image", "video", "file"].includes(a.effectiveOutputType) || a.morphedInputType === "file-upload";
	}

	private renderDetailModal() {
		const r = this.detailRecord.value;
		if (!r) return null;
		const attrs = this.detailAttrs;
		const img = this.cardImage(r);
		return html`
			<div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this.closeDetail(); }}>
				<div class="modal detail-modal">
					<div class="modal__header detail-modal__head">
						${img ? html`<img class="detail-modal__img" src=${img} alt="" />` : null}
						<div class="detail-modal__headinfo">
							<h2>${this.titleAttr ? String(r[this.titleAttr.attributeKey] ?? "—") : r.id}</h2>
							${this.statusAttr ? html`<clam-dynamic-viewer .attr=${this.statusAttr} .value=${r[this.statusAttr.attributeKey]} .resolver=${this.resolverFn}></clam-dynamic-viewer>` : null}
						</div>
						<button class="modal__close" title="Cerrar" @click=${() => this.closeDetail()}>×</button>
					</div>
					<div class="modal__body">
						<div class="detail-grid">${attrs.map((a) => html`
							<div class="detail-grid__item ${this.isMediaAttr(a) ? "detail-grid__item--full" : ""}">
								<span class="detail-grid__label">${a.attributeName}</span>
								<div class="detail-grid__value"><clam-dynamic-viewer .attr=${a} .value=${r[a.attributeKey]} .resolver=${this.resolverFn} ?detailed=${true}></clam-dynamic-viewer></div>
							</div>`)}</div>
					</div>
				<div class="modal__footer">
					<div class="modal-actions">
						<button class="btn btn-secondary" @click=${() => this.closeDetail()}>Cerrar</button>
						${canEdit(this.entityEndpoint) ? html`<button class="btn btn-secondary" @click=${() => { const rec = r; this.closeDetail(); this.openEdit(rec); }}>Editar</button>` : null}
						${canDelete(this.entityEndpoint) ? html`<button class="btn btn-danger" @click=${() => this.deleteRecord(r.id)}>Eliminar</button>` : null}
					</div>
				</div>
				</div>
			</div>`;
	}

	private renderForm() {
		const def = this.activeDef;
		if (!def) return null;
		return html`
			<div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) (this.showForm.value = false); }}>
				<div class="modal">
					<form @submit=${(e: SubmitEvent) => this.saveForm(e)}>
						<div class="modal__header">
							<h2>${this.editingRecord ? "Editar" : "Crear"} ${this.displayTitle}</h2>
							<button type="button" class="modal__close" title="Cerrar" @click=${() => (this.showForm.value = false)}>×</button>
						</div>
						<div class="modal__body">
							<div class="form-grid">${this.editableAttrs.filter((a) => this._shouldShowAttr(a)).map((attr) => this.renderField(attr, this.formData[attr.attributeKey], (v: unknown) => this.updateFormField(attr.attributeKey, v)))}</div>
						</div>
						<div class="modal__footer">
							<div class="modal-actions">
								<button type="button" class="btn btn-secondary" ?disabled=${this._saving} @click=${() => (this.showForm.value = false)}>Cancelar</button>
								<button type="submit" class="btn btn-primary" ?disabled=${this._saving}>${this._saving ? html`<span class="spinner"></span> Guardando...` : (this.editingRecord ? "Guardar" : "Crear")}</button>
							</div>
						</div>
					</form>
				</div>
			</div>`;
	}

	private renderSingletonView() {
		const def = this.activeDef!;
		const exists = !!this.editingRecord;
		return html`
			<div class="toolbar">
				<div class="toolbar__left">
					<h1 class="toolbar__title">${this.displayTitle}</h1>
					<p class="toolbar__subtitle">${def.entityDescription}</p>
				</div>
			</div>
			${this.loading.value ? html`<div class="loading"><span class="spinner spinner--inline"></span> Cargando...</div>` : html`
				<form class="singleton-form" @submit=${(e: SubmitEvent) => this.saveForm(e)}>
					<div class="form-grid">${this.editableAttrs.filter((a) => this._shouldShowAttr(a)).map((attr) => this.renderField(attr, this.formData[attr.attributeKey], (v: unknown) => this.updateFormField(attr.attributeKey, v)))}</div>
					<div class="singleton-footer">
						<div class="modal-actions">
							<button type="submit" class="btn btn-primary" ?disabled=${this._saving}>${this._saving ? html`<span class="spinner"></span> Guardando...` : (exists ? "Guardar cambios" : "Crear")}</button>
						</div>
					</div>
				</form>
			`}
		`;
	}
}

customElements.define("clam-entity-manager", ClamEntityManager);
